import { Language } from '@zond/api-interfaces';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Separator,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  Toggle,
} from '@zond/ui';
import { Moon, Sun } from 'lucide-react';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatches } from 'react-router-dom';
import { useAuth } from '../auth-provider';
import { useLanguage } from '../i18n/language-provider';
import { LanguageSwitcher } from '../i18n/language-switcher';
import { Loading } from '../loading';
import { useMe } from '../modules/users/user.queries';
import { AppSidebar } from './navigation/dashboard/app-sidebar';

const THEME_STORAGE_KEY = 'theme';

export const Layout = () => {
  const { isLoading, data } = useMe();
  const { setUser, user } = useAuth();
  const { applyServerLanguage } = useLanguage();
  const { t } = useTranslation('common');
  const matches = useMatches();
  // Tracks the last server language we applied, so a background refetch can't
  // revert a language the user just picked in-session.
  const lastServerLangRef = React.useRef<string | null>(null);

  const [isDark, setIsDark] = React.useState(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme === 'dark') {
      document.documentElement.classList.add('dark');
      return true;
    }
    if (savedTheme === 'light') {
      document.documentElement.classList.remove('dark');
      return false;
    }
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    if (data) {
      setUser(data);
      if (data.language && data.language !== lastServerLangRef.current) {
        lastServerLangRef.current = data.language;
        applyServerLanguage(data.language as Language);
      }
    }
  }, [data, setUser, applyServerLanguage]);

  const toggleTheme = () => {
    const newTheme = !isDark;
    setIsDark(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark');
  };

  if (isLoading || !user) {
    return <Loading stage="auth" />;
  }

  // Filter matches that have crumb handle
  const crumbs = matches
    .filter((match) => Boolean((match.handle as any)?.crumb))
    .map((match) => (match.handle as any).crumb())
    // Only show Dashboard when actually on the dashboard page
    .filter((crumb, index, array) => {
      // Key off the link target rather than its (translated) text.
      const isDashboardLink = crumb?.props?.to === '/:workspaceId';
      // If it's the dashboard link, only keep it if it's the only crumb (meaning we're on the dashboard page)
      if (isDashboardLink) {
        return array.length === 1;
      }
      return true;
    });

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4 flex-1">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {crumbs.map((crumb, index) => (
                  <React.Fragment key={index}>
                    <BreadcrumbItem>
                      {index === crumbs.length - 1 ? <BreadcrumbPage>{crumb}</BreadcrumbPage> : crumb}
                    </BreadcrumbItem>
                    {index < crumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
            <div className="ml-auto flex items-center gap-2">
              <LanguageSwitcher variant="ghost" />
              <Toggle
                pressed={isDark}
                onPressedChange={toggleTheme}
                size="sm"
                aria-label={t('actions.toggleTheme')}
              >
                {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              </Toggle>
            </div>
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 pt-0 min-h-0">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};
